import cx from 'classnames'
import React from 'react'

import User from '@js/common/user'

import { useTrackOnClick } from '@f/hooks/tracking/useTrackOnClick'

import { VendorSalesInfo } from '@f-fertighaus/applications/SearchResults/partials/items/SalesInfo'
import { SearchResultsVendorDetails } from '@f-fertighaus/applications/SearchResults/partials/items/Types'
import {
  CardTemplate,
  HouseThumbnails,
  ReviewsRating,
  VendorInfoBasic,
  VendorInfoPremium,
  VendorInfoPro
} from '@f-fertighaus/applications/SearchResults/partials/items/VendorTile'

const isPremium = (vendor: SearchResultsVendorDetails) =>
  vendor.branding_plan === 'premium'

interface SearchResultsVendorProps {
  item: SearchResultsVendorDetails
  position: number
  openSetLocationModal: () => void
}

const SearchResultsVendor = ({
  item: vendor,
  openSetLocationModal
}: SearchResultsVendorProps) => {
  const userLocation = User.getLocation()

  const handleItemClick = (event: React.MouseEvent<HTMLElement>) => {
    let cardLocation = 'vendor-results-standard'

    if (vendor.isTopPlacement) {
      cardLocation = 'vendor-results-top'
    } else if (vendor.top_local_placement) {
      cardLocation = 'vendor-results-regio'
    }

    const wtData = {
      category: 'item',
      action: 'click',
      location: cardLocation,
      label: 'card-vendorlist',
      values: [{ type: 'vendor_nid', value: vendor.nid }]
    }

    useTrackOnClick({ event, wtData })

    window.location.href = vendor.url
  }

  const hasExactlyThreeHouseThumbnails =
    vendor.search_result_additional_images &&
    vendor.search_result_additional_images.length === 3

  const displayHouseThumbnails =
    isPremium(vendor) && hasExactlyThreeHouseThumbnails

  return (
    <CardTemplate
      vendor={vendor}
      onClick={handleItemClick}
      className={cx(
        !displayHouseThumbnails &&
          'SearchResultsTile-image-container--has-full-radius'
      )}>
      {displayHouseThumbnails && (
        <HouseThumbnails
          vendorName={vendor.name}
          houseThumbnails={vendor.search_result_additional_images}
          onClick={handleItemClick}
        />
      )}
      {
        {
          basic: (
            <VendorInfoBasic
              name={vendor.display_name || vendor.name}
              slogan={vendor.slogan}
              onClick={handleItemClick}
              postcode={userLocation.postcode}
              top_local_placement={vendor.top_local_placement}>
              {window.__REVIEWS_ACTIVE__ && vendor.reviews.display_reviews && (
                <ReviewsRating
                  avg_rating={vendor.reviews.avg_rating}
                  count_total={vendor.reviews.count_total}
                  onClick={handleItemClick}
                />
              )}
            </VendorInfoBasic>
          ),
          pro: (
            <VendorInfoPro
              name={vendor.display_name || vendor.name}
              slogan={vendor.slogan}
              number_of_houses={vendor.number_of_houses}
              number_of_employees={vendor.number_of_employees}
              year_of_foundation={vendor.year_of_foundation}
              postcode={userLocation.postcode}
              top_local_placement={vendor.top_local_placement}
              onClick={handleItemClick}>
              {window.__REVIEWS_ACTIVE__ && vendor.reviews.display_reviews && (
                <ReviewsRating
                  avg_rating={vendor.reviews.avg_rating}
                  count_total={vendor.reviews.count_total}
                  onClick={handleItemClick}
                />
              )}
            </VendorInfoPro>
          ),
          premium: (
            <VendorInfoPremium
              name={vendor.display_name || vendor.name}
              slogan={vendor.slogan}
              number_of_houses={vendor.number_of_houses}
              number_of_employees={vendor.number_of_employees}
              year_of_foundation={vendor.year_of_foundation}
              branding_main_color={vendor.branding_colors.branding_main_color}
              postcode={userLocation.postcode}
              top_local_placement={vendor.top_local_placement}
              onClick={handleItemClick}>
              {window.__REVIEWS_ACTIVE__ && vendor.reviews.display_reviews && (
                <ReviewsRating
                  avg_rating={vendor.reviews.avg_rating}
                  count_total={vendor.reviews.count_total}
                  onClick={handleItemClick}
                />
              )}
            </VendorInfoPremium>
          )
        }[vendor.branding_plan]
      }
      {isPremium(vendor) && (
        <VendorSalesInfo
          nid={vendor.nid}
          postcode={userLocation.postcode}
          onLocationNotSetAction={openSetLocationModal}
          redirectUrl={vendor.url}
        />
      )}
    </CardTemplate>
  )
}

export default SearchResultsVendor
